import { useEffect, useMemo, useRef, useState } from 'react'

// Календарный range-пикер: год сверху → месяцы → дни. Работает в YYYY-MM-DD.
// Стиль под проект (rounded-xl, gray-borders, brand-акцент, выходные красным).
// Открывается поповером под кнопкой; Esc / клик вне — закрыть без применения.
// Используется в «Планировании» и на «Панели управления».

const RU_MONTHS_SHORT = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
]
const RU_MONTHS_FULL = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]
const RU_WEEKDAYS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС']

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** YYYY-MM-DD из year + month(0-based) + day. */
function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`
}

/** Дни месяца в виде сетки (Пн-первый). null — пустые ячейки до 1-го числа. */
function buildMonthGrid(year: number, month: number): (number | null)[] {
  const first = new Date(Date.UTC(year, month, 1))
  const startWeekday = (first.getUTCDay() + 6) % 7 // Пн=0 … Вс=6
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const cells: (number | null)[] = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  return cells
}

/** «2 янв 2026» из YYYY-MM-DD. */
function humanDate(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  return `${d} ${RU_MONTHS_SHORT[(m ?? 1) - 1]?.toLowerCase()} ${y}`
}

export interface DateRangePopoverProps {
  from: string | null
  to: string | null
  onApply: (from: string, to: string) => void
  onClose: () => void
  /** Доп. классы позиционирования (по умолчанию right-0). */
  align?: 'left' | 'right'
}

export function DateRangePopover({
  from,
  to,
  onApply,
  onClose,
  align = 'right',
}: DateRangePopoverProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Черновик выбора — применяется только по кнопке «Применить».
  const [draftFrom, setDraftFrom] = useState<string | null>(from)
  const [draftTo, setDraftTo] = useState<string | null>(to)

  // Левый календарь: год + месяц (правый = следующий месяц).
  const init = (from ?? to ?? ymd(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)).split(
    '-',
  )
  const [viewYear, setViewYear] = useState(parseInt(init[0]!, 10))
  const [viewMonth, setViewMonth] = useState(parseInt(init[1]!, 10) - 1)

  // Закрытие по Esc / клику вне.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [onClose])

  const shiftMonth = (delta: number) => {
    let y = viewYear
    let m = viewMonth + delta
    while (m < 0) {
      m += 12
      y -= 1
    }
    while (m > 11) {
      m -= 12
      y += 1
    }
    setViewYear(y)
    setViewMonth(m)
  }

  const pickDay = (iso: string) => {
    // Нет начала или оба выбраны → начинаем заново с начала.
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(iso)
      setDraftTo(null)
      return
    }
    // Начало есть, конца нет.
    if (iso < draftFrom) {
      setDraftFrom(iso)
      return
    }
    setDraftTo(iso)
  }

  const inRange = (iso: string): boolean => {
    if (!draftFrom) return false
    const end = draftTo ?? draftFrom
    const lo = draftFrom < end ? draftFrom : end
    const hi = draftFrom < end ? end : draftFrom
    return iso >= lo && iso <= hi
  }

  const isEdge = (iso: string): boolean => iso === draftFrom || iso === draftTo

  const canApply = Boolean(draftFrom)
  const apply = () => {
    if (!draftFrom) return
    onApply(draftFrom, draftTo ?? draftFrom)
    onClose()
  }

  // Какие месяцы текущего viewYear попадают в выбранный диапазон — для подсветки чипов.
  const monthInRange = useMemo(() => {
    return (monthIdx: number): boolean => {
      if (!draftFrom) return false
      const end = draftTo ?? draftFrom
      const lo = draftFrom < end ? draftFrom : end
      const hi = draftFrom < end ? end : draftFrom
      const mStart = ymd(viewYear, monthIdx, 1)
      const last = new Date(Date.UTC(viewYear, monthIdx + 1, 0)).getUTCDate()
      const mEnd = ymd(viewYear, monthIdx, last)
      return mStart <= hi && mEnd >= lo
    }
  }, [draftFrom, draftTo, viewYear])

  const months: { year: number; month: number }[] = [
    { year: viewYear, month: viewMonth },
    viewMonth === 11 ? { year: viewYear + 1, month: 0 } : { year: viewYear, month: viewMonth + 1 },
  ]

  return (
    <div
      ref={ref}
      className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} z-50 mt-2 w-[560px] max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white shadow-xl p-4`}
    >
      {/* Год */}
      <div className="flex items-center justify-center gap-3 mb-2">
        <button
          onClick={() => setViewYear((y) => y - 1)}
          className="w-7 h-7 rounded-lg text-gray-500 hover:bg-gray-100"
          title="Предыдущий год"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">{viewYear}</span>
        <button
          onClick={() => setViewYear((y) => y + 1)}
          className="w-7 h-7 rounded-lg text-gray-500 hover:bg-gray-100"
          title="Следующий год"
        >
          ›
        </button>
      </div>

      {/* Месяцы года — быстрый переход + подсветка диапазона */}
      <div className="grid grid-cols-6 gap-1 mb-3">
        {RU_MONTHS_SHORT.map((label, idx) => {
          const active = monthInRange(idx)
          return (
            <button
              key={idx}
              onClick={() => setViewMonth(idx)}
              className={`py-1 text-xs rounded-lg transition-colors ${
                active
                  ? 'bg-brand-50 text-brand-700 font-medium'
                  : viewMonth === idx
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Два месяца с днями */}
      <div className="flex gap-4">
        <button
          onClick={() => shiftMonth(-1)}
          className="self-start mt-1 w-7 h-7 shrink-0 rounded-lg text-gray-500 hover:bg-gray-100"
          title="Назад"
        >
          ‹
        </button>
        {months.map(({ year, month }, i) => (
          <div key={i} className="flex-1">
            <div className="text-center text-sm font-medium text-gray-800 mb-2">
              {RU_MONTHS_FULL[month]} {year}
            </div>
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {RU_WEEKDAYS.map((w, wi) => (
                <div
                  key={w}
                  className={`text-center text-[10px] font-medium ${wi >= 5 ? 'text-red-400' : 'text-gray-400'}`}
                >
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {buildMonthGrid(year, month).map((day, di) => {
                if (day === null) return <div key={di} />
                const iso = ymd(year, month, day)
                const weekend = di % 7 >= 5
                const edge = isEdge(iso)
                const ranged = inRange(iso)
                return (
                  <button
                    key={di}
                    onClick={() => pickDay(iso)}
                    className={`h-8 text-xs rounded-lg transition-colors ${
                      edge
                        ? 'bg-brand-600 text-white font-semibold'
                        : ranged
                          ? 'bg-brand-50 text-brand-700'
                          : weekend
                            ? 'text-red-500 hover:bg-gray-100'
                            : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        <button
          onClick={() => shiftMonth(1)}
          className="self-start mt-1 w-7 h-7 shrink-0 rounded-lg text-gray-500 hover:bg-gray-100"
          title="Вперёд"
        >
          ›
        </button>
      </div>

      {/* Подвал: выбранный диапазон + действия */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <div className="text-xs text-gray-600">
          {draftFrom ? (
            <>
              c <span className="font-medium text-gray-900">{humanDate(draftFrom)}</span> по{' '}
              <span className="font-medium text-gray-900">{humanDate(draftTo ?? draftFrom)}</span>
            </>
          ) : (
            <span className="text-gray-400">Выберите дату начала</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
          >
            Отмена
          </button>
          <button
            onClick={apply}
            disabled={!canApply}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-900 text-white disabled:opacity-40 hover:bg-gray-800"
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  )
}
